import Modal from "@via/components/modal";
import { StepProgress } from "@via/components/step-progress";
import { ModalSize } from "@via/utils/constants";
import { useEffect, useMemo, useState } from "react";
import DisplayConflicts from "@via/pages/projects/pipelines/workflow/components/import-pipeline/display-conflicts";
import PipelineSummary from "@via/pages/projects/pipelines/workflow/components/import-pipeline/pipeline-summary";
import Approval from "@via/pages/projects/pipelines/workflow/components/import-pipeline/approval";
import FileUploader from "@via/components/file-uploader/file-uploader";
import {
  useUploadViaPipeline,
  useImportViaPipeline,
} from "@via/pages/projects/pipelines/pipelines.api.tsx";
import {
  CheckImportViaPipelineEntitiesCheckResult,
  CheckImportViaPipelineResponseBody,
  ImportConflictResolution,
  ImportViaPipelineRequestBody,
  ImportViaPipelineRequestQuery,
} from "common";
import { ChevronRightIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";

/** The step that shows the conflicts, and the one it must not hand over to unanswered. */
const CONFLICTS_STEP_INDEX = 2;

const ImportViaPipelineModal = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [pipelineSummary, setPipelineSummary] =
    useState<CheckImportViaPipelineResponseBody | null>(null);
  const [conflicts, setConflicts] = useState<
    CheckImportViaPipelineEntitiesCheckResult[]
  >([]);

  const [menuGroups, setMenuGroups] = useState<
    Partial<ImportViaPipelineRequestBody["menuGroups"]>
  >({});

  // process_rev_uuid to the choice the user made for it. Nothing is preselected: the
  // backend refuses a reported conflict with no entry rather than assuming one, and the
  // UI matches that. A default here would let somebody click past a decision about a
  // process revision without ever making it.
  const [conflictResolutions, setConflictResolutions] = useState<
    Record<string, ImportConflictResolution>
  >({});

  const {
    mutate: uploadViaPipeline,
    data: uploadViaPipelineData,
    isSuccess: isUploadViaPipelineSuccess,
  } = useUploadViaPipeline();

  const {
    mutate: importViaPipeline,
    data: importViaPipelineData,
    isSuccess: isImportViaPipelineSuccess,
  } = useImportViaPipeline();

  const steps = useMemo(() => {
    return [
      {
        name: "Import Pipeline File",
        isCompleted: currentStepIndex > 0,
      },
      {
        name: "Pipeline Summary",
        isCompleted: currentStepIndex > 1,
      },
      {
        name: "Conflicts",
        isCompleted: currentStepIndex > 2,
      },
      {
        name: "Approval",
        isCompleted: currentStepIndex > 3,
      },
    ];
  }, [currentStepIndex]);

  useEffect(() => {
    if (isUploadViaPipelineSuccess && uploadViaPipelineData) {
      setCurrentStepIndex(1);

      setPipelineSummary(uploadViaPipelineData);

      // isConflict covers both an overwritable conflict and a revision identity
      // conflict, which carries no warningToUser and used to skip this step entirely.
      const conflicts = uploadViaPipelineData.categories
        .flatMap(category => category.items)
        .filter(item => item.isConflict);

      setConflicts(conflicts);
      // A fresh file means fresh conflicts, so answers to the previous one must not
      // survive and be sent against revisions this bundle never mentioned.
      setConflictResolutions({});

      const menuGroups = uploadViaPipelineData.categories.reduce(
        (acc, category) => {
          category.items.forEach(item => {
            acc[category.category] = acc[category.category] || [];
            acc[category.category].push({
              id: item.id,
              menuGroupName: item.menuGroupName,
            });
          });
          return acc;
        },
        {}
      );
      setMenuGroups(menuGroups);
    }
  }, [isUploadViaPipelineSuccess, uploadViaPipelineData]);

  useEffect(() => {
    if (isImportViaPipelineSuccess && importViaPipelineData) {
      const { pipelineId } = importViaPipelineData;
      setOpen(false);
      navigate(`/pipelines/${pipelineId}/description`);
    }
  }, [isImportViaPipelineSuccess]);

  // Revision identity conflicts only. An overwritable conflict carries no
  // revisionConflict and needs no choice, so it must not hold the import up.
  const revisionConflicts = useMemo(
    () =>
      conflicts
        .map(item => item.revisionConflict)
        .filter((conflict): conflict is NonNullable<typeof conflict> =>
          Boolean(conflict)
        ),
    [conflicts]
  );

  const unresolvedConflictCount = useMemo(
    () =>
      revisionConflicts.filter(
        conflict => !conflictResolutions[conflict.revisionUUID]
      ).length,
    [revisionConflicts, conflictResolutions]
  );

  // Held on the Conflicts step only. Blocking any earlier would trap the user before the
  // screen that offers the choice, and the Approval step is only reachable from here.
  const isBlockedOnConflicts =
    currentStepIndex === CONFLICTS_STEP_INDEX && unresolvedConflictCount > 0;

  const handleFileUpload = (files: File[]) => {
    const file = files[0];
    if (!file) {
      return;
    }
    uploadViaPipeline(file);
  };

  const handleApproveImport = () => {
    // The step gate below should have made this unreachable. Kept because approving
    // with a conflict unanswered is a 409 and a wasted round trip either way.
    if (unresolvedConflictCount > 0) {
      return;
    }

    const payload: ImportViaPipelineRequestBody &
      ImportViaPipelineRequestQuery = {
      menuGroups: {} as any,
      fileId: uploadViaPipelineData?.fileId || "",
    };

    if (revisionConflicts.length > 0) {
      payload.conflictResolutions = conflictResolutions;
    }

    if (menuGroups.pipeline) {
      payload.menuGroups["pipeline"] = menuGroups.pipeline;
    }
    if (menuGroups?.process) {
      payload.menuGroups["process"] = menuGroups.process;
    }
    if (menuGroups?.modules) {
      payload.menuGroups["modules"] = menuGroups.modules;
    }
    if (menuGroups?.aiModel) {
      payload.menuGroups["aiModel"] = menuGroups.aiModel;
    }
    if (menuGroups?.modelSkill) {
      payload.menuGroups["modelSkill"] = menuGroups.modelSkill;
    }
    importViaPipeline(payload);
  };

  const handleMenuGroupUpdate = (
    category: string,
    itemId: string,
    newMenuGroup: string
  ) => {
    setMenuGroups(prev => {
      if (!prev[category]) return prev;

      const updatedCategory = prev[category].map(item =>
        String(item.id) === String(itemId)
          ? { ...item, menuGroupName: newMenuGroup }
          : item
      );

      return {
        ...prev,
        [category]: updatedCategory,
      };
    });
  };

  const handleResolutionChange = (
    revisionUUID: string,
    resolution: ImportConflictResolution
  ) => {
    setConflictResolutions(prev => ({ ...prev, [revisionUUID]: resolution }));
  };

  const renderStepContent = () => {
    switch (currentStepIndex) {
      case 0:
        return (
          <FileUploader
            onUpload={handleFileUpload}
            maxFiles={1}
            isLoading={false}
            progressPercentageList={[]}
          />
        );
      case 1:
        return (
          <PipelineSummary
            summary={pipelineSummary}
            onMenuGroupUpdate={handleMenuGroupUpdate}
            menuGroupUpdates={menuGroups}
          />
        );
      case 2:
        return (
          <DisplayConflicts
            conflicts={conflicts}
            resolutions={conflictResolutions}
            onResolutionChange={handleResolutionChange}
          />
        );
      case 3:
        return (
          <Approval
            handleApproveImport={handleApproveImport}
            setOpen={setOpen}
            unresolvedConflictCount={unresolvedConflictCount}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      title="Import Pipeline"
      size={ModalSize.Large}
      hasCancelButton={false}>
      <div className="space-y-4">
        <StepProgress steps={steps} currentStepIndex={currentStepIndex} />
        <div className="mt-6">{renderStepContent()}</div>
      </div>
      {currentStepIndex !== 0 && (
        <div className="flex items-center justify-between pt-6 border-t">
          {currentStepIndex === 1 ? (
            <div></div>
          ) : (
            <button
              className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm border border-gray-600  bg-white-600 hover:bg-white-500 focus-visible:outline-white-600 text-gray shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2"
              onClick={() => setCurrentStepIndex(currentStepIndex - 1)}>
              Previous
            </button>
          )}
          {currentStepIndex < steps.length - 1 && (
            <div className="flex items-center gap-x-3">
              {isBlockedOnConflicts && (
                <span
                  className="text-sm text-red-700"
                  data-testid="conflictsBlockNextStep">
                  {unresolvedConflictCount === 1
                    ? "Choose what to do with the conflicting process version first."
                    : `Choose what to do with the ${unresolvedConflictCount} conflicting process versions first.`}
                </span>
              )}
              <button
                disabled={isBlockedOnConflicts}
                data-testid="importGoToNextStep"
                className="inline-flex items-center gap-x-1.5 rounded-md px-3 py-2 text-sm bg-indigo-600 hover:bg-indigo-500 focus-visible:outline-indigo-600 text-white shadow-sm duration-200 ease-in-out focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:bg-gray-400 disabled:cursor-not-allowed disabled:hover:bg-gray-400"
                onClick={() => setCurrentStepIndex(currentStepIndex + 1)}>
                Go to Next Step
                <ChevronRightIcon className="ml-2 h-4 w-4" />
              </button>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ImportViaPipelineModal;
